import { useLazyQuery, useMutation } from "@apollo/client";
import { useWindowWidth } from "@react-hook/window-size";
import { gqlUser, gqlVendor } from "gql";
import { ICreateVendorSaasInput } from "gql/Vendor/mutations";
import useTranslation from "next-translate/useTranslation";
import { useEffect, useRef, useState } from "react";

import {
  ONLY_LETTERS_AND_DASH_REGEX,
  USER_TOKEN_PERSIST,
  WIDTH_MD
} from "@constants/global";
import { Loader2 } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, useForm } from "react-hook-form";
import { Checkbox, Input, InputGroup, Steps } from "rsuite";
import { Button } from "components/ui/v2";
import { ECompanySegment, IVendor, companySegment } from "types/Vendor.types";
import { getQueryOperator } from "utils/helpers";
import useToast from "utils/hooks/useToast";
import { termsAndConditionsEn } from "utils/terms-en";
import { termsAndConditions } from "utils/terms-es";
import { buildFirstLoginUrl } from "utils/safe-navigation";
import RadioBox from "./RadioBox";

interface IOrganizationFormProps {
  callback?: (error?: string) => void;
}
const totalSteps = 4;

interface IForm {
  name: string;
  employees: number;
  companySegment: ECompanySegment;
  aceptPolicy: boolean;
  reCaptcha: string;
}
const OrganizationForm = (props: IOrganizationFormProps) => {
  const { toast } = useToast();
  const [error, setError] = useState<string>("");
  const { callback } = props;
  const [step, setStep] = useState<number>(1);
  const { t, lang } = useTranslation("common");
  const [captcha, setCaptcha] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [completed, setCompleted] = useState<boolean>(false);
  const [domain, setDomain] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(true);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    watch,
    trigger,
    setValue
  } = useForm<IForm>({
    defaultValues: {
      name: "",
      employees: 5,
      companySegment: companySegment.SOFTWARE["name"],
      aceptPolicy: false
    }
  });

  const [validateVendorName, { loading: validateVendorNameLoading }] =
    useLazyQuery(gqlVendor.queries.VALIDATE_VENDOR_NAME, {
      onCompleted: ({ validateVendorName }) => {
        if (validateVendorName) {
          setError(t("El nombre de la empresa ya existe, intenta otro"));
          setCaptcha(null);
          recaptchaRef.current?.reset();
        } else {
          setStep(step + 1);
        }
      },
      onError: ({ message }) => {
        toast.error(message);
      }
    });

  const [createVendorSaas, { loading: createVendorSaasLoading }] = useMutation<
    { createVendorSaas: IVendor },
    { createVendorSaasInput: ICreateVendorSaasInput }
  >(gqlVendor.mutations.CREATE_VENDOR_SASS, {
    refetchQueries: [
      getQueryOperator(gqlUser.queries.GET_FULL_USER),
      getQueryOperator(gqlUser.queries.USER_ONBOARDING)
    ],
    awaitRefetchQueries: true,
    onCompleted({ createVendorSaas }) {
      const { id, domains } = createVendorSaas;
      if (id) {
        // * go to created domain
        setCompleted(true);
        setStep(step + 1);
        if (domains && domains.length > 0) {
          setDomain(domains[0]);
        }
      }
    },
    onError: ({ message }) => {
      if (callback) {
        callback(message);
      }
    }
  });

  const handleFinish = (data: IForm) => {
    createVendorSaas({
      variables: {
        createVendorSaasInput: {
          ...data
        }
      }
    });
  };

  const handleEmployeeClick = (value: string | number) => {
    if (typeof value === "number") {
      setValue("employees", value);
    }
  };

  const handleCompanySegmentClick = (value: ECompanySegment) => {
    setValue("companySegment", value);
  };

  const handleNext = async () => {
    if (step < totalSteps) {
      if (step === 1) {
        const error = await trigger("name");

        if (error) {
          validateVendorName({
            variables: {
              name: watch("name"),
              captcha: captcha || ""
            }
          });
          setError("");
        } else {
          setError(t("Debes ingresar un nombre de empresa válido"));
        }
      }

      if (step === 2) {
        const error = await trigger("employees");

        if (error) {
          setStep(step + 1);
          setError("");
        } else {
          setError(t("Debes seleccionar el numero de empleados"));
        }
      }

      if (step === 3) {
        const error = await trigger("companySegment");
        if (error) {
          setStep(step + 1);
          setError("");
        } else {
          setError(t("Debes seleccionar una ndustria"));
        }
      }
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleCaptcha = (data: string) => {
    if (data) {
      setCaptcha(data);
    } else {
      setCaptcha(data);
    }
  };

  const wwidth = useWindowWidth();
  const isMd = wwidth >= WIDTH_MD;

  // * useEffect when completed === true
  // * we need to count 3 minutes and then
  // * set creating to false
  useEffect(() => {
    if (completed) {
      setTimeout(() => {
        setCreating(false);
      }, 180000);
    }
  }, [completed]);

  return (
    <div className="flex h-full w-full flex-col p-3 md:p-6">
      {isMd && !completed && (
        <Steps current={step - 1} className="pb-10">
          <Steps.Item title={t("Organización")} />
          <Steps.Item title={t("Tamaño")} />
          <Steps.Item title={t("Tipo")} />
          <Steps.Item title={t("Terminos")} />
        </Steps>
      )}

      {!isMd && !completed && (
        <Steps current={step - 1} className="pb-10">
          <Steps.Item />
          <Steps.Item />
          <Steps.Item />
          <Steps.Item />
        </Steps>
      )}

      <div className="flex w-full flex-1 flex-col items-center justify-start pb-6 md:justify-center">
        <div className="w-full">
          {step === 1 && (
            <div
              className="animate__animated animate__fadeInRight animate__faster w-full"
              id="step1"
            >
              <label className="mb-3 text-left font-bold">
                {t("Ingresa el nombre de tu organización")}
              </label>

              <Controller
                name="name"
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: ONLY_LETTERS_AND_DASH_REGEX,
                    message: t(
                      "El nombre de la organización solo puede contener letras y guiones"
                    )
                  }
                }}
                render={({ field }) => (
                  <InputGroup>
                    <Input {...field} placeholder={t("ej: groowcity-x")} />
                    <InputGroup.Addon>.groowcity.com</InputGroup.Addon>
                  </InputGroup>
                )}
              />
              <small className="w-full text-green-500">
                * {t("Solo puedes usar letras y guiones")}
              </small>
              <div className="flex items-center justify-end gap-2">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Ldu9p4UAAAAANobJCf3Weyt5ptMNMsk9OlYaZoh"
                  className="mt-2"
                  hl={lang}
                  onChange={handleCaptcha}
                />
              </div>
            </div>
          )}
          {step === 2 && (
            <div
              className="animate__animated animate__fadeInRight animate__faster w-full overflow-y-auto"
              id="step2"
            >
              <label className="block text-left font-bold">
                {t("Cantidad de empleados")}
              </label>
              <div className="flex w-full flex-wrap">
                <RadioBox
                  icon="user"
                  title={t("De 1 a 5")}
                  className="w-full md:w-1/2 lg:w-1/4"
                  value={5}
                  currentValue={watch("employees")}
                  onClick={handleEmployeeClick}
                />
                <RadioBox
                  icon="users"
                  title={t("De 6 a 15")}
                  className="w-full md:w-1/2 lg:w-1/4"
                  value={15}
                  currentValue={watch("employees")}
                  onClick={handleEmployeeClick}
                />
                <RadioBox
                  icon="users"
                  title={t("De 16 a 50")}
                  className="w-full md:w-1/2 lg:w-1/4"
                  value={50}
                  currentValue={watch("employees")}
                  onClick={handleEmployeeClick}
                />
                <RadioBox
                  icon="users"
                  title="+51"
                  className="w-full md:w-1/2 lg:w-1/4"
                  value={99999}
                  currentValue={watch("employees")}
                  onClick={handleEmployeeClick}
                />
              </div>
            </div>
          )}
          {step === 3 && (
            <div
              className="animate__animated animate__fadeInRight animate__faster w-full"
              id="step3"
            >
              <label className="mb-3 block text-left font-bold">
                {t("¿A qué industria pertenece?")}
              </label>
              <div className="flex w-full flex-wrap">
                {Object.keys(companySegment).map((segment) => (
                  <RadioBox
                    key={companySegment[segment].name}
                    icon={companySegment[segment].icon}
                    title={t(companySegment[segment].name)}
                    className="w-full md:w-1/3 xl:w-1/4"
                    value={companySegment[segment].name}
                    currentValue={watch("companySegment")}
                    onClick={(value) => handleCompanySegmentClick(value as ECompanySegment)}
                  />
                ))}
              </div>
            </div>
          )}
          {step === 4 && (
            <div
              className="animate__animated animate__fadeInRight animate__faster w-full"
              id="step4"
            >
              <label className="mb-3 block text-left font-bold">
                {t("Términos y condiciones")}
              </label>
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    lang === "en" ? termsAndConditionsEn : termsAndConditions
                }}
                style={{ height: 300, overflowY: "auto" }}
                className="mb-3 rounded-md bg-gray-100 p-6 dark:bg-gray-900"
              />
              <Controller
                name="aceptPolicy"
                control={control}
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <div className="flex items-center justify-center gap-2 md:justify-end">
                    <Checkbox
                      onChange={(value) => {
                        field.onChange(!!value);
                        trigger("aceptPolicy");
                      }}
                      value="terms"
                    >
                      <span>{t("ACEPT_TERMS_AND_CONDITIONS")}</span>
                    </Checkbox>
                  </div>
                )}
              />
              <Controller
                name="reCaptcha"
                control={control}
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <div className="flex items-center justify-center gap-2 md:justify-end">
                    <ReCAPTCHA
                      {...field}
                      sitekey="6Ldu9p4UAAAAANobJCf3Weyt5ptMNMsk9OlYaZoh"
                      className="mt-2"
                      hl={lang}
                      onChange={(value) => {
                        field.onChange(value);
                        trigger("reCaptcha");
                      }}
                    />
                  </div>
                )}
              />
            </div>
          )}
          {step === 5 && completed && (
            <>
              <div className="animate__animated animate__fadeInRight animate__faster flex w-full flex-col items-center justify-center">
                <div className="flex flex-col items-center justify-center md:w-2/3">
                  <h3 className="mb-3 block text-center text-2xl font-bold">
                    {t("¡Excelente! Estamos Preparando Tu Nueva Organización")}
                  </h3>
                  <p className="mb-3 text-center">
                    {t(
                      "¡Descubre tu nuevo mundo en Groowcity! Haz clic en 'Siguiente' y comienza tu aventura"
                    )}
                  </p>
                  {creating && (
                    <span className="pb-4 text-center font-semibold">
                      {t(
                        "Creando tu organización, esto puede tardar unos minutos"
                      )}
                    </span>
                  )}
                  <Button
                    variant="primary"
                    className="w-2/3"
                    onClick={() => {
                        window.open(
                          buildFirstLoginUrl(
                            domain,
                            localStorage.getItem(USER_TOKEN_PERSIST) ?? ""
                          )
                        );
                    }}
                    size="lg"
                    disabled={creating}
                  >
                    {/* counter 3 minutes */}
                    {creating ? (
                      <>
                        {t("Creando organización")}{" "}
                        <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                      </>
                    ) : (
                      t("Ir a mi organización")
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
        <small className="w-full text-red-500">{error}</small>
      </div>
      <div className="flex items-center justify-center gap-3 pb-6 md:justify-end md:p-0">
        <div className="flex items-center justify-center gap-3 md:justify-end">
          {step > 1 && !completed && (
            <Button
              style={{ width: 150, height: 52 }}
              onClick={handlePrev}
              size="lg"
            >
              {t("Anterior")}
            </Button>
          )}
          {step < totalSteps && (
            <Button
              variant="primary"
              style={{ width: 150 }}
              onClick={handleNext}
              loading={validateVendorNameLoading}
              size="lg"
              disabled={step === 1 && !captcha}
            >
              {t("Siguiente")}
            </Button>
          )}

          {step === totalSteps && (
            <Button
              variant="primary"
              style={{ width: 180 }}
              onClick={handleSubmit(handleFinish)}
              disabled={!isValid}
              loading={createVendorSaasLoading}
              size="lg"
            >
              {t("Finalizar y crear")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationForm;
